import React, { useMemo } from "react";
import { CheckCircle, Error } from "@mui/icons-material";
import { Box, Paper, Tooltip } from "@mui/material";
import classNames from "classnames";
import { groupBy } from "lodash";

import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { GroupLabel } from "fond/project/panels/InputPanel/GroupLabel";
import { ImportStatus } from "fond/types";
import { isImportInProgress } from "fond/utils/layers";
import { LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { muPowerInputLayers } from "./layers";

interface InputPanelProps {
  isReadOnly?: boolean;
  inputLayers: Map<string, LayerStatus>;
}

const getRightAdornment = ({ isError, isRequired, isComplete }: { isError: boolean; isRequired: boolean; isComplete: boolean }) => {
  if (isError) {
    return (
      <Tooltip title="Import failed">
        <Error color="error" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  }

  if (!isComplete && isRequired) {
    return (
      <Tooltip title="Required">
        <Error color="action" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  }

  if (isComplete) {
    return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
  }

  return null;
};

const InputPanel: React.FC<InputPanelProps> = ({ isReadOnly, inputLayers }) => {
  const { open } = useStackedNavigationContext();

  const isAnyUploading = Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));

  const groupedLayers = useMemo(() => groupBy(muPowerInputLayers, "group"), []);

  return (
    <Box data-testid="mu-power-input-panel">
      {Object.entries(groupedLayers).map(([groupName, layers], index) => (
        <Box key={groupName}>
          <GroupLabel
            variant="caption"
            className={classNames({
              first: index === 0,
            })}
          >
            {groupName}
          </GroupLabel>
          {layers.map(({ key, icon, title, isRequired }) => {
            const layerStatus = inputLayers.get(key);
            const hasLayer = !!layerStatus?.layer;
            const isError = layerStatus?.status === ImportStatus.ERROR;
            const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

            return (
              <StackedNavigationButton
                key={`mu-power-${key}-input`}
                data-testid={`mu-power-${key.split("/").pop()}-input`}
                icon={icon}
                title={title}
                onClick={() => open(key)}
                sx={{ m: 0, mt: 1 }}
                disabled={isReadOnly || isAnyUploading}
                checked={isComplete}
                rightElement={getRightAdornment({ isError, isComplete, isRequired: !!isRequired })}
              />
            );
          })}
        </Box>
      ))}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}
    </Box>
  );
};

export default InputPanel;
